import React, { useCallback, useContext, useState, useEffect, useRef } from 'react'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import LineNightIcon from 'LuxKit/Icons/line/LineNightIcon'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import noop from 'lib/function/noop'
import { useIntl } from 'react-intl'

interface Props {
  duration: number
  checkInDate?: string
  inSidebar?: boolean
  open?: boolean
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  iconDisabled?: boolean
  ref?: React.Ref<HTMLButtonElement | HTMLDivElement>
}

function OfferDurationSelectBox(props: Props) {
  const intl = useIntl()
  const { duration, checkInDate, open, onClick = noop, inSidebar, iconDisabled, ref } = props
  // Show a placeholder until we see the user actually select something or they interact with filter
  const [hasInteracted, setHasInteracted] = useState<boolean>(false)
  const initialDuration = useRef(duration)

  useEffect(() => {
    if ((checkInDate || duration !== initialDuration.current) && !hasInteracted) {
      setHasInteracted(true)
    }
  }, [checkInDate, duration, hasInteracted])

  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const onBoxClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    setHasInteracted(true)
    if (open) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('DURATION_FILTER_CLOSED')
    } else {
      trackEvent('DURATION_FILTER_PRESSED')
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'duration' })
    }
    onClick(e)
  }, [dispatch, onClick, open, trackEvent])

  const durationLabel = intl.formatMessage({
    id: '6FzMZVAvwQs__NvVho3h',
    defaultMessage: '{count, plural, one {# night} other {# nights}}',
    description: 'Selected duration value in duration filter dropdown',
  }, { count: duration })

  return <OfferFilterDropdownBox
    inSidebar={inSidebar}
    ref={ref}
    onClick={onBoxClick}
    icon={!iconDisabled && <LineNightIcon />}
    active={open}
    data-testid="OfferDurationSelectBox"
    label={intl.formatMessage({
      id: '4BoCZo_J-W0BNjSbKJB3',
      defaultMessage: 'Duration',
      description: 'Form field label for duration filter dropdown on the offer page',
    })}
    placeholder={intl.formatMessage({
      id: 'mR1F3xAgaS6ivedcR1v_',
      defaultMessage: 'Select',
      description: 'Placeholder text for duration filter dropdown on offer page',
    })}
    value={(!hasInteracted && !inSidebar) ? undefined : <b>{durationLabel}</b>}
  />
}

export default withOfferPageState(state => ({
  duration: state.duration,
  checkInDate: state.checkInDate,
  open: state.activeFilter === 'duration',
}), OfferDurationSelectBox)
